import type { ReactElement } from 'react';
import { createElement } from 'react';

import type { FontSizesType } from '@/modules/perseo-billing/components/form/ui.interface';

import type { ITypographyProps } from './typography.interface';

const elementTag: { [key in FontSizesType]: string } = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  body1: 'p',
  body2: 'p',
  caption: 'small'
};

export const Typography = ({
  family = 'montserrat',
  size = 'body1',
  decoration = 'no-underline',
  weight = 'normal',
  color = 'default',
  children,
  align = 'left',
  leading = '24px',
  noWrap = false,
  lineClamp = 2,
  heightInherit = false,
  className = '',
  onClick = (event: MouseEvent) => event,
  ...props
}: ITypographyProps): ReactElement => {
  function createElementTag(): string {
    return elementTag[size];
  }

  function createColorFont(): string {
    if (
      color === 'my_credits_basic' ||
      color === 'my_credits_intermediate' ||
      color === 'my_credits_advanced' ||
      color === 'my_credits_expert' ||
      color === 'my_credits_preferential' ||
      color === 'my_credits_elite'
    ) {
      return color;
    }

    if (color === 'black' || color === 'default') {
      return `common-${color}`;
    }
    if (color === 'white') {
      return 'common-white';
    }
    if (color === 'inherit') {
      return 'current';
    }
    if (color === 'magenta') {
      return 'error-dark';
    }
    if (color === 'silver') {
      return 'gray-dark';
    }
    if (color === 'blue-indigo') {
      return 'blue-indigo';
    }
    if (color === 'cyan') {
      return 'cyan_blue';
    }
    if (color === 'example-grey') {
      return 'example-grey';
    }
    if (color === 'primary-500') {
      return 'primary-500';
    }
    if (color === 'gray-dark') {
      return 'gray-dark';
    }
    if (color === 'pds-error-600') {
      return 'pds-error-600';
    }
    if (color === 'pds-neutral-400') {
      return 'pds-neutral-400';
    }
    if (color === 'neutral-grey-500') {
      return 'neutral-grey-500';
    }
    if (color === 'pds-neutral-700') {
      return 'pds-neutral-700';
    }
    if (color === 'pds-neutral-800') {
      return 'pds-neutral-800';
    }
    if (color === 'pds-neutral-900') {
      return 'pds-neutral-900';
    }
    if (color === 'pds-success-400') {
      return 'pds-success-400';
    }
    if (color === 'pds-success-700') {
      return 'pds-success-700';
    }
    if (color === 'pds-information-500') {
      return 'pds-information-500';
    }
    if (color === 'pds-information-600') {
      return 'pds-information-600';
    }
    if (color === 'pds-primary-600') {
      return 'pds-primary-600';
    }
    if (color === 'pds-warning-600') {
      return 'pds-warning-600';
    }
    if (color === 'pds-tertiary-600') {
      return 'pds-tertiary-600';
    }

    return `${color}-main`;
  }

  function truncateText(): string {
    if (noWrap) {
      return `co-block co-line-clamp-${lineClamp} co-break-words co-overflow-hidden co-overflow-ellipsis`;
    }
    return 'co-flex co-items-center';
  }

  function createAlignText(): string {
    if (align === 'right') {
      return 'co-text-right';
    }
    if (align === 'center') {
      return 'co-text-center';
    }
    return 'co-text-left';
  }

  function heightInheritPromoText(): string {
    if (heightInherit) {
      return 'co-max-h-5';
    }
    return '';
  }

  const colorClassName = `co-text-${createColorFont()}`;
  const textSizeClassName = `co-text-${size}`;
  const fontFamilyClassName = `co-font-${family}`;
  const fontWeightClassName = `co-font-${weight}`;
  const leadingClassName = `co-leading-${leading}`;

  return createElement(
    createElementTag(),
    {
      className: `
            ${colorClassName}
            ${textSizeClassName}
            ${fontFamilyClassName}
            ${fontWeightClassName}
            ${truncateText()}
            ${createAlignText()}
            ${leadingClassName}
            co-${decoration}
            ${heightInheritPromoText()}
            ${className ? className : ''}
            `,
      onClick,
      tabIndex: props.tabIndex,
      'aria-label': props.ariaLabel
    },
    children
  );
};
